/* One shared clock. Absolute timestamps avoid drift after backgrounding/sleep.
   No requests, storage, inventory writes, cart interception or automatic restart. */
(() => {
  if (customElements.get('loc-preorder-countdown')) return;
  const active = new Set();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  let clock;
  const tick = () => {
    const now = Date.now();
    for (const card of active) card.update(now);
    if (!active.size) { clearInterval(clock); clock = undefined; }
  };
  const syncClock = () => {
    clearInterval(clock);
    clock = undefined;
    if (!document.hidden && active.size) {
      tick();
      if (active.size) clock = setInterval(tick, 1000);
    }
  };
  // Observe visibility once instead of synchronously measuring each timer
  // between DOM writes every second. Hidden worlds/search results stay idle.
  const visibility = new IntersectionObserver(entries => {
    for (const {target: card, isIntersecting, intersectionRect} of entries) {
      if (!card.isConnected) { active.delete(card); continue; }
      card.inViewport = isIntersecting && intersectionRect.width > 0 && intersectionRect.height > 0;
      if (card.inViewport && card.dataset.state !== 'ended') {
        card.hasTicked = false; // Catch up from the absolute deadline, without replaying missed flips.
        active.add(card);
      } else {
        active.delete(card);
        card.units?.forEach(unit => card.stopFlip(unit));
      }
    }
    syncClock();
  });
  class ReleaseCountdown extends HTMLElement {
    connectedCallback() {
      this.deadline = Date.parse(this.dataset.releaseAt);
      this.digits = this.querySelector('[data-release-digits]');
      this.label = this.querySelector('[data-release-label]');
      this.units = ['days', 'hours', 'minutes', 'seconds'].map(unit => this.querySelector(`[data-release-unit="${unit}"]`));
      if (!Number.isFinite(this.deadline) || !this.digits || !this.label || this.units.some(unit => !unit)) return;
      this.inViewport = false;
      this.hasTicked = false;
      this.update(Date.now());
      if (this.dataset.state !== 'ended') visibility.observe(this);
    }
    disconnectedCallback() {
      active.delete(this);
      visibility.unobserve(this);
      this.inViewport = false;
      this.units?.forEach(unit => this.stopFlip(unit));
      if (!active.size) { clearInterval(clock); clock = undefined; }
    }
    stopFlip(unit) {
      if (!unit) return;
      unit.getAnimations({subtree: true}).forEach(animation => animation.cancel());
      unit.querySelectorAll('.loc-release__flap').forEach(flap => flap.remove());
    }
    setDigit(unit, text, animate) {
      const previous = unit.dataset.value;
      if (previous === text) return;
      this.stopFlip(unit);
      unit.dataset.value = text;
      const value = document.createElement('span');
      value.className = 'loc-release__value';
      value.textContent = text;
      unit.replaceChildren(value);
      if (!animate || previous === undefined || !unit.animate) return;
      const flap = (half, number) => {
        const element = document.createElement('span');
        element.className = `loc-release__flap loc-release__flap--${half}`;
        element.setAttribute('aria-hidden', 'true');
        const face = document.createElement('span');
        face.textContent = number;
        element.append(face); unit.append(element);
        return element;
      };
      // Old lower half stays until the new leaf folds down over it.
      const oldBottom = flap('bottom', previous);
      const top = flap('top', previous);
      const bottom = flap('bottom', text);
      const upper = top.animate([{transform:'rotateX(0deg)'},{transform:'rotateX(-90deg)'}],
        {duration:160,easing:'cubic-bezier(.55,0,1,.45)',fill:'forwards'});
      const lower = bottom.animate([{transform:'rotateX(90deg)'},{transform:'rotateX(0deg)'}],
        {duration:200,delay:160,easing:'cubic-bezier(.16,1,.3,1)',fill:'both'});
      lower.onfinish = () => { upper.cancel(); lower.cancel(); top.remove(); bottom.remove(); oldBottom.remove(); };
    }
    update(now) {
      const remaining = Math.max(0, Math.ceil((this.deadline - now) / 1000));
      if (!remaining) {
        this.dataset.state = 'ended';
        this.label.textContent = this.dataset.labelEnded;
        this.digits.hidden = true;
        this.units.forEach(unit => this.stopFlip(unit));
        active.delete(this);
        return;
      }
      if (this.dataset.state !== 'active') {
        this.dataset.state = 'active';
        this.label.textContent = this.dataset.labelActive;
        this.digits.hidden = false;
      }
      const values = [Math.floor(remaining / 86400), Math.floor(remaining / 3600) % 24, Math.floor(remaining / 60) % 60, remaining % 60];
      const animate = this.dataset.presentation !== 'text' && this.hasTicked && this.inViewport && !document.hidden && !reducedMotion.matches;
      this.units.forEach((unit, index) => {
        const text = String(values[index]).padStart(2, '0');
        this.setDigit(unit, text, animate);
      });
      this.hasTicked = true;
    }
  }
  customElements.define('loc-preorder-countdown', ReleaseCountdown);
  document.addEventListener('visibilitychange', () => {
    for (const card of active) {
      card.hasTicked = false;
      card.units?.forEach(unit => card.stopFlip(unit));
    }
    syncClock();
  });
})();
